import React, { FC, useState, useEffect } from 'react'
import Select from 'react-select'
import { Tag, InboxUser } from '../hooks/useChatGraphQL'

type Props = {
    filterUnread: boolean
    onToggleUnread: (val: boolean) => void

    filterDateFrom: string
    onSetDateFrom: (val: string) => void

    filterDateTo: string
    onSetDateTo: (val: string) => void

    filterTags: number[]
    onSetTags: (tags: number[]) => void

    availableTags: Tag[]

    filterWhatsapp: boolean
    onToggleWhatsapp: (active: boolean) => void
    filterSms: boolean
    onToggleSms: (active: boolean) => void

    // Inbox Mode
    inboxMode: 'my' | 'general' | 'all'
    onSetMode: (mode: 'my' | 'general' | 'all') => void
    isAdmin: boolean
    isSupervisor: boolean

    // User filter (admin/supervisor only)
    filterUserId: number | null
    onSetFilterUserId: (id: number | null) => void
    inboxUsers: InboxUser[]
    onFetchInboxUsers: () => void

    // Org filter (superadmin only)
    isSuperAdmin: boolean
    filterOrgId: number | null
    onSetFilterOrgId: (id: number | null) => void
    orgOptions: { value: number; label: string }[]
    onFetchOrgOptions: () => void
}

const ChatFilters: FC<Props> = ({
    filterUnread,
    onToggleUnread,
    filterDateFrom,
    onSetDateFrom,
    filterDateTo,
    onSetDateTo,
    filterTags,
    onSetTags,
    availableTags,
    filterWhatsapp,
    onToggleWhatsapp,
    filterSms,
    onToggleSms,
    inboxMode,
    onSetMode,
    isAdmin,
    isSupervisor,
    filterUserId,
    onSetFilterUserId,
    inboxUsers,
    onFetchInboxUsers,
    isSuperAdmin,
    filterOrgId,
    onSetFilterOrgId,
    orgOptions,
    onFetchOrgOptions,
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const canFilterByUser = isAdmin || isSupervisor || inboxUsers.length > 0

    // Lazy-load users and orgs the first time the panel opens
    useEffect(() => {
        if (isOpen && canFilterByUser && inboxUsers.length === 0) {
            onFetchInboxUsers()
        }
        if (isOpen && isSuperAdmin && orgOptions.length === 0) {
            onFetchOrgOptions()
        }
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    const toggleTag = (tagId: number) => {
        if (filterTags.includes(tagId)) {
            onSetTags(filterTags.filter(id => id !== tagId))
        } else {
            onSetTags([...filterTags, tagId])
        }
    }

    const clearFilters = () => {
        onToggleUnread(false)
        onSetDateFrom('')
        onSetDateTo('')
        onSetTags([])
        onToggleWhatsapp(false)
        onToggleSms(false)
        onSetFilterUserId(null)
        onSetFilterOrgId(null)
    }

    const activeFiltersCount =
        (filterUnread ? 1 : 0) +
        (filterWhatsapp ? 1 : 0) +
        (filterSms ? 1 : 0) +
        (filterDateFrom ? 1 : 0) +
        (filterDateTo ? 1 : 0) +
        filterTags.length +
        (filterUserId ? 1 : 0) +
        (filterOrgId ? 1 : 0)

    // Build react-select options: value = id, label = "Full Name — email"
    const userOptions = inboxUsers.map(u => ({
        value: u.id,
        label: `${u.full_name} — ${u.email}`,
    }))

    const selectedUserOption = filterUserId
        ? userOptions.find(o => o.value === filterUserId) ?? null
        : null

    return (
        <div className='position-relative d-inline-block'>
            <button
                className={`btn btn-sm ${activeFiltersCount > 0 ? 'btn-primary' : 'btn-light'} d-flex align-items-center gap-2`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <i className='bi bi-funnel fs-3'></i>
                Filters
                {activeFiltersCount > 0 && (
                    <span className='badge badge-circle badge-white text-primary ms-1'>{activeFiltersCount}</span>
                )}
            </button>

            {isOpen && (
                <div
                    className='card shadow-sm position-absolute p-4'
                    style={{
                        zIndex: 100,
                        top: '100%',
                        left: 0,
                        width: '320px',
                        marginTop: '5px'
                    }}
                >
                    <div className='d-flex justify-content-between align-items-center mb-4'>
                        <h4 className='card-title m-0 fs-5 fw-bold text-gray-800'>Advanced Filters</h4>
                        <button className='btn btn-icon btn-sm btn-light-danger' onClick={() => setIsOpen(false)}>
                            <i className='bi bi-x fs-2'></i>
                        </button>
                    </div>

                    {/* Status */}
                    <div className='mb-4'>
                        <label className='form-label fw-bold text-gray-700'>Status</label>
                        <div className='form-check form-switch form-check-custom form-check-solid'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                checked={filterUnread}
                                onChange={(e) => onToggleUnread(e.target.checked)}
                                id='filterUnread'
                            />
                            <label className='form-check-label text-gray-700' htmlFor='filterUnread'>
                                Unread Only
                            </label>
                        </div>
                    </div>

                    {/* Channels */}
                    <div className='mb-4'>
                        <label className='form-label fw-bold text-gray-700'>Channels</label>
                        <div className='d-flex gap-4'>
                            <div className='form-check form-check-custom form-check-solid form-check-sm'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={filterWhatsapp}
                                    onChange={(e) => onToggleWhatsapp(e.target.checked)}
                                    id='whatsappCheck'
                                />
                                <label className='form-check-label text-gray-700' htmlFor='whatsappCheck'>
                                    WhatsApp
                                </label>
                            </div>
                            <div className='form-check form-check-custom form-check-solid form-check-sm'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={filterSms}
                                    onChange={(e) => onToggleSms(e.target.checked)}
                                    id='smsCheck'
                                />
                                <label className='form-check-label text-gray-700' htmlFor='smsCheck'>
                                    SMS
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Date Range */}
                    <div className='mb-4'>
                        <label className='form-label fw-bold text-gray-700'>Date Range</label>
                        <div className='d-flex gap-2 flex-column'>
                            <input
                                type='date'
                                className='form-control form-control-sm form-control-solid'
                                value={filterDateFrom}
                                onChange={(e) => onSetDateFrom(e.target.value)}
                            />
                            <input
                                type='date'
                                className='form-control form-control-sm form-control-solid'
                                value={filterDateTo}
                                onChange={(e) => onSetDateTo(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Tags */}
                    <div className='mb-4'>
                        <label className='form-label fw-bold text-gray-700'>Tags</label>
                        <div className='d-flex flex-wrap gap-2'>
                            {availableTags.map(tag => (
                                <span
                                    key={tag.id}
                                    className={`badge cursor-pointer ${filterTags.includes(tag.id) ? 'badge-primary' : 'badge-light'}`}
                                    style={filterTags.includes(tag.id)
                                        ? { backgroundColor: tag.color }
                                        : { border: `1px solid ${tag.color}`, color: tag.color }}
                                    onClick={() => toggleTag(tag.id)}
                                >
                                    {tag.name}
                                </span>
                            ))}
                            {availableTags.length === 0 && (
                                <span className='text-muted fs-7'>No tags available</span>
                            )}
                        </div>
                    </div>

                    {/* User filter — admin and supervisor only */}
                    {canFilterByUser && (
                        <div className='mb-5'>
                            <label className='form-label fw-bold text-gray-700'>
                                User
                                {isSupervisor && !isAdmin && (
                                    <span className='text-muted fs-8 ms-2'>(your team)</span>
                                )}
                            </label>
                            <Select
                                isClearable
                                isSearchable
                                isLoading={canFilterByUser && inboxUsers.length === 0}
                                options={userOptions}
                                value={selectedUserOption}
                                onChange={(opt) => onSetFilterUserId(opt ? opt.value : null)}
                                placeholder='Search by name or email...'
                                noOptionsMessage={() => 'No users found'}
                                loadingMessage={() => 'Loading...'}
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '36px',
                                        fontSize: '13px',
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        fontSize: '13px',
                                        backgroundColor: state.isFocused ? '#f1f3f9' : 'white',
                                        color: '#333',
                                    }),
                                }}
                            />
                        </div>
                    )}

                    {/* Organization filter — superadmin only */}
                    {isSuperAdmin && (
                        <div className='mb-5'>
                            <label className='form-label fw-bold text-gray-700'>Organization</label>
                            <Select
                                isClearable
                                isSearchable
                                isLoading={isSuperAdmin && orgOptions.length === 0}
                                options={orgOptions}
                                value={filterOrgId ? orgOptions.find(o => o.value === filterOrgId) ?? null : null}
                                onChange={(opt) => onSetFilterOrgId(opt ? opt.value : null)}
                                placeholder='All organizations'
                                noOptionsMessage={() => 'No organizations found'}
                                loadingMessage={() => 'Loading...'}
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({ ...base, minHeight: '36px', fontSize: '13px' }),
                                    option: (base, state) => ({
                                        ...base,
                                        fontSize: '13px',
                                        backgroundColor: state.isFocused ? '#f1f3f9' : 'white',
                                        color: '#333',
                                    }),
                                }}
                            />
                        </div>
                    )}

                    {/* Actions */}
                    <div className='d-flex justify-content-end'>
                        <button className='btn btn-sm btn-light me-2' onClick={clearFilters}>
                            Clear
                        </button>
                        <button className='btn btn-sm btn-primary' onClick={() => setIsOpen(false)}>
                            Apply
                        </button>
                    </div>
                </div>
            )}

            {/* Overlay to close when clicking outside */}
            {isOpen && (
                <div
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 90 }}
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    )
}

export default ChatFilters
